import React, { useEffect, useState } from "react";
import PageHeader from "../../components/ui/PageHeader";
import SectionHeader from "../../components/ui/SectionHeader";
import GlobalLoader from "../../components/ui/GlobalLoader";
import { Button } from "../../components/ui/Button";
import { useToast } from "../../hooks/useToast";
import {
  generateKioskAppId,
  getKioskSettings,
  KioskLayoutMode,
  KioskSettingsData,
  updateKioskSettings,
} from "../../services/settings";
import { Check, Copy, KeyRound, Monitor, RefreshCw } from "lucide-react";

const KioskSettings = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [data, setData] = useState<KioskSettingsData | null>(null);
  const [layoutMode, setLayoutMode] = useState<KioskLayoutMode>("landscape");

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getKioskSettings();
        if (!mounted) return;
        setData(res);
        setLayoutMode(res.layout_mode === "portrait" ? "portrait" : "landscape");
      } catch (err) {
        console.error(err);
        toast.error("Failed to load kiosk settings");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCopy = async () => {
    if (!data?.app_id) return;
    try {
      await navigator.clipboard.writeText(data.app_id);
      setCopied(true);
      toast.success("App ID copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy App ID");
    }
  };

  const handleGenerate = async () => {
    const rotating = !!data?.app_id;
    if (
      rotating &&
      !window.confirm(
        "Generate a new App ID? Existing kiosks paired with the old ID will need to re-activate."
      )
    ) {
      return;
    }

    setGenerating(true);
    try {
      const res = await generateKioskAppId();
      setData(res);
      setLayoutMode(res.layout_mode === "portrait" ? "portrait" : "landscape");
      toast.success(rotating ? "App ID rotated" : "App ID generated");
    } catch (err: any) {
      toast.error(err?.message || "Failed to generate App ID");
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateKioskSettings({ layout_mode: layoutMode });
      setData(res);
      toast.success("Kiosk settings saved");
    } catch (err: any) {
      toast.error(err?.message || "Failed to save kiosk settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 min-h-[400px]">
        <GlobalLoader
          initialPageLoad={false}
          description="Loading kiosk settings..."
          compensateSidebar={false}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Kiosk Settings"
        description="Pair kiosk devices with an App ID and choose display layout."
        variant="card"
      />

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <SectionHeader title="App ID" />
          <p className="mt-1 text-sm text-gray-600">
            Enter this code on the kiosk to link it to this salon. Format:{" "}
            <span className="font-mono text-gray-800">XXXX-XXXX-XXXX-XXXX</span>{" "}
            (A–Z / 0–9).
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <KeyRound className="h-4 w-4 text-gray-400 shrink-0" />
                <span className="font-mono text-base tracking-wider text-gray-900 truncate">
                  {data?.app_id || "Not generated yet"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={handleCopy}
                disabled={!data?.app_id}
                className="gap-2"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                Copy
              </Button>
              <Button
                type="button"
                onClick={handleGenerate}
                disabled={generating}
                className="gap-2"
              >
                <RefreshCw
                  className={`h-4 w-4 ${generating ? "animate-spin" : ""}`}
                />
                {data?.app_id ? "Rotate" : "Generate"}
              </Button>
            </div>
          </div>
          {data?.currency ? (
            <p className="text-xs text-gray-500">
              Currency sent to kiosks:{" "}
              <span className="uppercase font-medium text-gray-700">
                {data.currency}
              </span>
            </p>
          ) : null}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <SectionHeader title="Display layout" />
          <p className="mt-1 text-sm text-gray-600">
            Default orientation for activated kiosk devices.
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(
              [
                { value: "landscape", label: "Landscape", hint: "Wide / tablet horizontal" },
                { value: "portrait", label: "Portrait", hint: "Tall / phone vertical" },
              ] as const
            ).map((opt) => {
              const selected = layoutMode === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setLayoutMode(opt.value)}
                  className={`text-left rounded-xl border px-4 py-4 transition-colors ${
                    selected
                      ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Monitor
                      className={`h-4 w-4 ${
                        selected ? "text-blue-600" : "text-gray-400"
                      }`}
                    />
                    <span
                      className={`font-medium ${
                        selected ? "text-blue-900" : "text-gray-900"
                      }`}
                    >
                      {opt.label}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">{opt.hint}</p>
                </button>
              );
            })}
          </div>

          <div className="flex justify-end pt-2">
            <Button type="button" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save layout"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KioskSettings;
